<template>
  <!-- The Workspace369 inbox list (the app's Inbox screen, see public/home/screens/inbox-*.png):
       title with the compose, contacts, broadcast and settings buttons, search, the Open /
       Unread / Voicemail / Snoozed / Closed filters, and conversations tagged by channel (SMS,
       Call, VM, Email). People, numbers and messages are sample data. Inside a StoryScene the
       conversations arrive one by one, newest last, and the newest gets its unread mark; resting
       styles are the final frame. Chips use square corners (site rule, no pill shapes). -->
  <div class="ilw" aria-hidden="true">
    <header class="ilw-head">
      <b>Inbox</b>
      <span class="ilw-tools">
        <svg viewBox="0 0 24 24"><path :d="icons.mail" /></svg>
        <svg viewBox="0 0 24 24"><path :d="icons.personAdd" /></svg>
        <svg viewBox="0 0 24 24"><path :d="icons.broadcast" /></svg>
        <svg viewBox="0 0 24 24"><path :d="icons.gear" /></svg>
      </span>
    </header>
    <div class="ilw-search"><svg viewBox="0 0 24 24"><path :d="icons.search" /></svg>Search conversations...</div>
    <div class="ilw-filters">
      <span v-for="(filter, i) in filters" :key="filter" class="ilw-filter" :class="{ 'is-on': i === 0 }">{{ filter }}</span>
    </div>

    <ol class="ilw-list">
      <li
        v-for="(row, i) in rows"
        :key="row.name"
        class="ilw-row"
        :class="{ 'is-new': i === 0 }"
        :style="{ '--d': `${0.25 + (rows.length - 1 - i) * 0.7}s` }"
      >
        <span class="ilw-avatar" :style="{ '--tone': row.tone }">{{ row.initials }}</span>
        <span class="ilw-copy">
          <span class="ilw-line"><b>{{ row.name }}</b><time>{{ row.time }}</time></span>
          <span class="ilw-preview">{{ row.preview }}</span>
          <span class="ilw-tags">
            <em v-for="tag in row.tags" :key="tag" class="ilw-tag" :class="`ilw-tag--${tag.toLowerCase()}`">{{ tag }}</em>
          </span>
        </span>
        <i v-if="i === 0" class="ilw-dot" />
      </li>
    </ol>
  </div>
</template>

<script setup>
// The inbox filters as the app shows them; the last one is cut off by the pane edge.
const filters = ["Open", "Unread", "Voicemail", "Snoozed", "Closed"];
const rows = [
  { name: "Maya Ortiz", initials: "MO", time: "9:41", preview: "Can we move the shoot to Thursday the 29th?", tags: ["SMS"], tone: "#3f58bf" },
  { name: "Northwind Studio", initials: "NS", time: "9:32", preview: "Re: Spring campaign proposal", tags: ["Email"], tone: "#2d7a4e" },
  { name: "+1 (512) 555-0147", initials: "+", time: "9:20", preview: "Sorry we missed your call! We'll get back to you shortly.", tags: ["Call", "SMS"], tone: "#5f6368" },
  { name: "Leo Grant", initials: "LG", time: "9:05", preview: "Voicemail: Hi, it's Leo, about Thursday's...", tags: ["VM", "Call"], tone: "#ad4e00" },
  { name: "Acme Co", initials: "AC", time: "8:47", preview: "Call ended · 4m 12s", tags: ["Call"], tone: "#7948af" },
];
const icons = {
  mail: "M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z",
  personAdd: "M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z",
  broadcast: "M7.76 16.24C6.67 15.16 6 13.66 6 12s.67-3.16 1.76-4.24l1.42 1.42C8.45 9.9 8 10.9 8 12c0 1.1.45 2.1 1.17 2.83l-1.41 1.41zm8.48 0C17.33 15.16 18 13.66 18 12s-.67-3.16-1.76-4.24l-1.42 1.42C15.55 9.9 16 10.9 16 12c0 1.1-.45 2.1-1.17 2.83l1.41 1.41zM12 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm8 2c0 2.21-.9 4.21-2.35 5.65l1.42 1.42C20.88 17.26 22 14.76 22 12s-1.12-5.26-2.93-7.07l-1.42 1.42C19.1 7.79 20 9.79 20 12zM6.35 6.35 4.93 4.93C3.12 6.74 2 9.24 2 12s1.12 5.26 2.93 7.07l1.42-1.42C4.9 16.21 4 14.21 4 12s.9-4.21 2.35-5.65z",
  gear: "M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.488.488 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z",
  search: "M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z",
};
</script>

<style>
/* App tokens shared with the other replicas (components/motion/AiChatWindow.vue). */
.ilw {
  --ilw-page: #fff;
  --ilw-wash: #f4f6f8;
  --ilw-line: rgb(0 0 0 / 0.1);
  --ilw-text: #171717;
  --ilw-muted: #5f6368;
  --ilw-ink: #171717;
  --ilw-ink-text: #fff;
  --ilw-sel: #ececec;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border: 1px solid var(--ilw-line);
  border-radius: 10px;
  background: var(--ilw-page);
  color: var(--ilw-text);
  font-family: "Space Grotesk", Inter, ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.35;
  text-align: left;
  box-shadow: 0 18px 50px -18px rgb(15 23 42 / 0.28), 0 2px 6px rgb(15 23 42 / 0.06);
}
.dark .ilw {
  --ilw-page: #1c1c1c;
  --ilw-wash: #262626;
  --ilw-line: rgb(255 255 255 / 0.13);
  --ilw-text: #ececec;
  --ilw-muted: #a8a8a8;
  --ilw-ink: #ececec;
  --ilw-ink-text: #171717;
  --ilw-sel: #2a2a2a;
  box-shadow: 0 18px 50px -18px rgb(0 0 0 / 0.7);
}
.ilw svg { width: 16px; height: 16px; flex: none; fill: currentColor; }
.ilw-head, .ilw-search, .ilw-filters { flex: none; }
.ilw-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px 8px; }
.ilw-head b { font-size: 18px; font-weight: 600; }
.ilw-tools { display: flex; gap: 14px; color: var(--ilw-muted); }
.ilw-search { display: flex; align-items: center; gap: 8px; margin: 4px 14px 10px; border-radius: 8px; background: var(--ilw-wash); padding: 8px 10px; color: var(--ilw-muted); font-size: 12.5px; }
.ilw-filters { display: flex; gap: 6px; overflow: hidden; padding: 0 14px 10px; white-space: nowrap; }
.ilw-filter { flex: none; border: 1px solid var(--ilw-line); border-radius: 6px; padding: 4px 10px; font-size: 12px; }
.ilw-filter.is-on { border-color: var(--ilw-ink); background: var(--ilw-ink); color: var(--ilw-ink-text); }
.ilw-list { display: grid; flex: 1; align-content: start; margin: 0; padding: 0; list-style: none; border-top: 1px solid var(--ilw-line); }
/* The list runs past the window, as a real inbox does: the last row fades out instead of being cut. */
.ilw-list { min-height: 0; overflow: hidden; -webkit-mask-image: linear-gradient(#000 calc(100% - 44px), transparent); mask-image: linear-gradient(#000 calc(100% - 44px), transparent); }
.ilw-row { position: relative; display: flex; gap: 10px; border-bottom: 1px solid var(--ilw-line); padding: 9px 14px; }
/* The newest row's selection is its own layer, so it can fade in on opacity alone. */
.ilw-row.is-new::before { content: ""; position: absolute; inset: 0; z-index: -1; background: var(--ilw-sel); box-shadow: inset 3px 0 0 var(--ilw-ink); }
.ilw-row.is-new { z-index: 0; }
.ilw-avatar { display: grid; place-items: center; flex: none; width: 32px; height: 32px; border-radius: 8px; background: color-mix(in srgb, var(--tone) 14%, transparent); color: var(--tone); font-size: 11px; font-weight: 700; }
.dark .ilw-avatar { background: color-mix(in srgb, var(--tone) 28%, transparent); color: #e5e7eb; }
.ilw-copy { display: grid; flex: 1; gap: 2px; min-width: 0; }
.ilw-line { display: flex; justify-content: space-between; gap: 8px; }
.ilw-line b { overflow: hidden; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.ilw-line time { flex: none; color: var(--ilw-muted); font-size: 11.5px; }
.ilw-preview { overflow: hidden; color: var(--ilw-muted); font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.is-new .ilw-preview { color: var(--ilw-text); font-weight: 500; }
.ilw-tags { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; margin-top: 1px; }
.ilw-tag { border-radius: 4px; padding: 0 5px; font-size: 10.5px; font-style: normal; font-weight: 500; line-height: 1.6; }
.ilw-tag--sms { background: #e8eefc; color: #3155c4; }
.ilw-tag--call { background: #f2eafb; color: #7a3fb0; }
.ilw-tag--vm { background: #fdf2d8; color: #8a5a00; }
.ilw-tag--email { background: #e6f4ea; color: #2d7a4e; }
.dark .ilw-tag--sms { background: rgb(91 127 234 / 0.2); color: #a9bdf8; }
.dark .ilw-tag--call { background: rgb(160 110 220 / 0.2); color: #d2b6f2; }
.dark .ilw-tag--vm { background: rgb(241 201 94 / 0.16); color: #f1c95e; }
.dark .ilw-tag--email { background: rgb(63 178 110 / 0.18); color: #6ed39a; }
.ilw-dot { position: absolute; top: 14px; right: 14px; width: 7px; height: 7px; border-radius: 2px; background: #1ba7ce; }
.is-new .ilw-line time { margin-right: 14px; }

/* Motion: each conversation fades in where it rests, oldest first; the newest gets its mark. */
.is-run .ilw-row { animation: ilw-in 0.5s cubic-bezier(0.33, 1, 0.68, 1) var(--d) both; }
.is-run .ilw-row.is-new::before { animation: ilw-sel 0.4s ease calc(var(--d) + 0.6s) both; }
.is-run .ilw-dot { animation: ilw-pop 0.35s ease calc(var(--d) + 0.7s) both; }
@keyframes ilw-in { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: none; } }
@keyframes ilw-sel { from { opacity: 0; } to { opacity: 1; } }
@keyframes ilw-pop { from { opacity: 0; transform: scale(0.4); } to { opacity: 1; transform: none; } }
@media (max-width: 480px) {
  .ilw-row { padding: 8px 10px; }
  .ilw-head { padding: 12px 12px 6px; }
  .ilw-search { margin: 4px 10px 8px; }
  .ilw-filters { padding: 0 10px 8px; }
}
</style>
